import { supportEmail } from "@clockwise/client-commons/src/constants/site";
import { Link } from "@clockwise/design-system";
import { SubscriptionInterval } from "@clockwise/schema";
import classNames from "classnames";
import { Info } from "phosphor-react";
import React from "react";
import {
  ITierType,
  businessTier,
  enterpriseTier,
  freeTier,
  pricingFaqs,
  proTier,
} from "../../constants/org-pricing.constants";
import ComparisonGrid from "./comparison-grid/ComparisonGrid";
import DowngradeDialog from "./downgrade-dialog/DowngradeDialog";
import Header from "./header/Header";
import { JoinExistingPlanBanner } from "./join-existing-plan-banner/JoinExistingPlanBanner";
import { M365BetaCallout } from "./m365-beta-callout/M365BetaCallout";
import { PricingFaq } from "./pricing-faq";
import BusinessTierHeader from "./tier-headers/BusinessTierHeader";
import EnterpriseTierHeader from "./tier-headers/EnterpriseTierHeader";
import FreeTierHeader from "./tier-headers/FreeTierHeader";
import ProTierHeader from "./tier-headers/ProTierHeader";
import { PlanTypes } from "./types";

export type PricingPlansProps = {
  billingRecurrence: SubscriptionInterval;
  daysLeftOnTrial: number;
  downgradeDialogOpen: boolean;
  isUserOnTrial: boolean;
  onContactUsClick: (tierType: ITierType) => void;
  onDowngradeClick: (tierType: ITierType) => void;
  onDowngradeConfirmation: () => void;
  onDowngradeDialogClose: () => void;
  onUpgradeClick: (tierType: ITierType) => void;
  primarySubscriptionPlan: PlanTypes;
  shouldShowJoinExistingPlanBanner: boolean;
  isFullWidth: boolean;
  isOnM365PaywallExclusion: boolean;
  isOnM365GALaunch: boolean;
};

const PricingPlans = ({
  billingRecurrence,
  daysLeftOnTrial,
  downgradeDialogOpen,
  isUserOnTrial,
  onContactUsClick,
  onDowngradeClick,
  onDowngradeConfirmation,
  onDowngradeDialogClose,
  onUpgradeClick,
  primarySubscriptionPlan,
  shouldShowJoinExistingPlanBanner,
  isFullWidth,
  isOnM365PaywallExclusion,
  isOnM365GALaunch,
}: PricingPlansProps) => {
  const maybeRenderNonStandardPlansCallout = () => {
    if (primarySubscriptionPlan !== "NonStandard") {
      return null;
    }

    return (
      <div
        className={classNames(
          "cw-body-lg cw-w-full cw-rounded cw-py-5 cw-mt-7 cw-mb-4 cw-text-center cw-bg-info",
          "cw-border cw-border-solid cw-border-info",
        )}
      >
        <Info className="-cw-mb-[6px]" /> You’re on a paid plan with custom terms. Contact{" "}
        <Link href={`mailto:${supportEmail}`} target="_blank">
          {supportEmail}
        </Link>{" "}
        to learn more about the details.
      </div>
    );
  };

  const renderPricingTierHeaders = () => {
    return (
      <div
        className={classNames(
          "cw-grid cw-grid-cols-1 cw-w-full cw-gap-y-2 cw-items-end",
          "md:cw-grid-cols-2 md:cw-gap-x-8",
          { "lg:cw-grid-cols-4": isFullWidth, "xl:cw-grid-cols-4": !isFullWidth },
        )}
      >
        <FreeTierHeader
          billingRecurrence={billingRecurrence}
          tier={freeTier}
          onSelect={() => onDowngradeClick(freeTier.type)}
          isCurrentPlan={primarySubscriptionPlan === "Free"}
          isFullWidth={isFullWidth}
        />
        <ProTierHeader
          billingRecurrence={billingRecurrence}
          daysLeftOnTrial={daysLeftOnTrial}
          isUserOnTrial={isUserOnTrial}
          onSelect={() => onUpgradeClick(proTier.type)}
          primarySubscriptionPlan={primarySubscriptionPlan}
          tier={proTier}
          isFullWidth={isFullWidth}
        />
        <BusinessTierHeader
          billingRecurrence={billingRecurrence}
          tier={businessTier}
          onSelect={() => onContactUsClick(businessTier.type)}
          isCurrentPlan={primarySubscriptionPlan === "Business"}
          isFullWidth={isFullWidth}
        />
        <EnterpriseTierHeader
          tier={enterpriseTier}
          onSelect={() => onContactUsClick(enterpriseTier.type)}
          isCurrentPlan={primarySubscriptionPlan === "Enterprise"}
          isFullWidth={isFullWidth}
        />
      </div>
    );
  };

  const renderFAQ = () => {
    return (
      <div className="cw-text-center cw-w-full cw-bg-neutral cw-flex cw-p-6 cw-mb-12 cw-rounded cw-flex-col md:cw-flex-row cw-mt-4">
        <div className="cw-heading-3xl">FAQ</div>
        <div className="cw-flex cw-flex-col cw-pl-4">
          {pricingFaqs.map((faq) => (
            <PricingFaq key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="cw-flex cw-flex-col cw-flex-1 cw-items-center cw-max-w-[1200px] cw-w-full">
      {maybeRenderNonStandardPlansCallout()}
      <Header />
      {shouldShowJoinExistingPlanBanner && <JoinExistingPlanBanner />}
      {!isOnM365GALaunch && isOnM365PaywallExclusion && primarySubscriptionPlan === "Free" && (
        <M365BetaCallout />
      )}
      {renderPricingTierHeaders()}
      <ComparisonGrid
        isUserOnTrial={isUserOnTrial}
        onContactUsClick={onContactUsClick}
        onDowngradeClick={onDowngradeClick}
        onUpgradeClick={onUpgradeClick}
        primarySubscriptionPlan={primarySubscriptionPlan}
      />
      {renderFAQ()}
      <DowngradeDialog
        isOpen={downgradeDialogOpen}
        onClose={onDowngradeDialogClose}
        onConfirm={onDowngradeConfirmation}
      />
    </div>
  );
};

export default PricingPlans;
